// Client-side file check. It copies the rules of the server file uploadPolicy.js:
// an allowlist of extensions, plus a check of the real content (magic bytes, SVG without script).
// This is for fast feedback only. A user can bypass it, so the server checks again.
// Keep it in sync with ALLOWED_TYPES, FORMATS and SVG_DANGEROUS in uploadPolicy.js.

// Number of bytes read from the start of a file to check its real type
const HEAD_BYTES = 512;
// SVG is checked as a whole file, so it needs a small size cap
const MAX_SVG_BYTES = 5 * 1024 * 1024;

// extension -> format (the format name is a key of FORMATS below)
const ALLOWED_FORMATS = new Map(
  Object.entries({
    // images
    '.jpg': 'jpeg',
    '.jpeg': 'jpeg',
    '.png': 'png',
    '.gif': 'gif',
    '.webp': 'webp',
    '.bmp': 'bmp',
    '.tif': 'tiff',
    '.tiff': 'tiff',
    '.svg': 'svg',
    '.heic': 'isobmff',
    '.heif': 'isobmff',
    '.avif': 'isobmff',
    // video
    '.mp4': 'isobmff',
    '.m4v': 'isobmff',
    '.mov': 'isobmff',
    '.3gp': 'isobmff',
    '.webm': 'matroska',
    '.mkv': 'matroska',
    '.avi': 'avi',
    // audio
    '.mp3': 'mpegAudio',
    '.aac': 'mpegAudio',
    '.m4a': 'isobmff',
    '.wav': 'wav',
    '.ogg': 'ogg',
    '.flac': 'flac',
    // documents
    '.pdf': 'pdf',
    '.doc': 'ole',
    '.xls': 'ole',
    '.ppt': 'ole',
    '.docx': 'zip',
    '.xlsx': 'zip',
    '.pptx': 'zip',
    '.odt': 'zip',
    '.ods': 'zip',
    '.odp': 'zip',
    '.rtf': 'rtf',
    // archive and medical
    '.zip': 'zip',
    '.dcm': 'dicom',
    '.dicom': 'dicom',
    // text
    '.txt': 'text',
    '.csv': 'text'
  })
);

// Things that make an SVG active (script, event handlers, external content, entities).
const SVG_DANGEROUS = [
  /<script[\s>/]/i,
  /<foreignObject[\s>/]/i,
  /<(iframe|embed|object|audio|video)[\s>/]/i,
  /\son[a-z]+\s*=/i, // onload=, onclick= ...
  /javascript\s*:/i,
  /data\s*:\s*text\/html/i,
  /<!ENTITY/i // XML entity tricks
];

function isSafeSvg(bytes) {
  if (bytes.includes(0x00)) return false;
  const text = new TextDecoder('utf-8').decode(bytes);
  return /<svg[\s>]/i.test(text) && !SVG_DANGEROUS.some((pattern) => pattern.test(text));
}

const ascii = (text) => Array.from(text).map((char) => char.charCodeAt(0));
const startsWith = (bytes, signature, offset = 0) =>
  bytes.length >= offset + signature.length &&
  signature.every((byte, i) => bytes[offset + i] === byte);
const hasAscii = (bytes, text, offset = 0) => startsWith(bytes, ascii(text), offset);

// Signature checks ("magic bytes"). They look at the file content, not at the browser mimetype.
const FORMATS = {
  jpeg: (b) => startsWith(b, [0xff, 0xd8, 0xff]),
  png: (b) => startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
  gif: (b) => hasAscii(b, 'GIF87a') || hasAscii(b, 'GIF89a'),
  webp: (b) => hasAscii(b, 'RIFF') && hasAscii(b, 'WEBP', 8),
  bmp: (b) => hasAscii(b, 'BM'),
  tiff: (b) => startsWith(b, [0x49, 0x49, 0x2a, 0x00]) || startsWith(b, [0x4d, 0x4d, 0x00, 0x2a]),
  // MP4 / MOV / M4A / 3GP / HEIC / AVIF all use the ISO base media container
  isobmff: (b) =>
    ['ftyp', 'moov', 'mdat', 'wide', 'free', 'skip'].some((box) => hasAscii(b, box, 4)),
  matroska: (b) => startsWith(b, [0x1a, 0x45, 0xdf, 0xa3]), // WebM / MKV
  avi: (b) => hasAscii(b, 'RIFF') && hasAscii(b, 'AVI ', 8),
  wav: (b) => hasAscii(b, 'RIFF') && hasAscii(b, 'WAVE', 8),
  mpegAudio: (b) => hasAscii(b, 'ID3') || (b.length > 1 && b[0] === 0xff && (b[1] & 0xe0) === 0xe0),
  ogg: (b) => hasAscii(b, 'OggS'),
  flac: (b) => hasAscii(b, 'fLaC'),
  pdf: (b) => hasAscii(b, '%PDF-'),
  // DOCX / XLSX / PPTX / ODF / ZIP are zip containers
  zip: (b) => startsWith(b, [0x50, 0x4b, 0x03, 0x04]) || startsWith(b, [0x50, 0x4b, 0x05, 0x06]),
  ole: (b) => startsWith(b, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]),
  rtf: (b) => hasAscii(b, '{\\rtf'),
  // DICOM Part 10 file: 128 byte preamble, then the "DICM" marker
  dicom: (b) => hasAscii(b, 'DICM', 128),
  // Plain text has no signature. We only make sure it is not binary (no NUL byte).
  text: (b) => !b.includes(0x00),
  // SVG is checked on the whole file (see isSafeSvg), not on the first bytes
  svg: isSafeSvg
};

// Same rule as path.extname on the server: the last dot, in lower case.
// "photo.JPG" -> ".jpg", "archive.tar.gz" -> ".gz", "README" -> "", ".gitignore" -> ""
export const getFileExtension = (filename) => {
  const name = String(filename || '');
  const index = name.lastIndexOf('.');
  return index > 0 ? name.slice(index).toLowerCase() : '';
};

// Extension check only (fast, no reading)
export const isAllowedFile = (file) => ALLOWED_FORMATS.has(getFileExtension(file && file.name));

const readBytes = (blob) => {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  // Older browsers
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
};

// Extension + real content. Resolves to true or false, it never throws.
// Only the first 512 bytes are read (the whole file only for SVG), so big files are fine.
export const isValidFile = async (file) => {
  try {
    const format = ALLOWED_FORMATS.get(getFileExtension(file && file.name));
    if (!format || !file.size) return false;
    if (format === 'svg') {
      if (file.size > MAX_SVG_BYTES) return false;
      return FORMATS.svg(await readBytes(file));
    }
    return FORMATS[format](await readBytes(file.slice(0, HEAD_BYTES)));
  } catch (err) {
    return false;
  }
};
